import React, { useState, useEffect } from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IconButton,
    Pivot,
    PivotItem,
    PrimaryButton,
    SearchBox,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Dropdown,
    IDropdownOption
} from '@fluentui/react';
import Text from '../../../../../../typography/text';
import Label from '../../../../../../typography/label';
import PackingListStorageService, { ISavedPackingListHistoryData, ISavedPackingListItem } from '../../../../../../../services/orders/packingListStorage';
import OrderService from '../../../../../../../services/orders/order';
import GeneralService from '../../../../../../../services/general';
import { IOrderResourceProps } from '../../../../../../../props/orders/order';
import { IOrderContainerResourceShortProps } from '../../../../../../../props/orders/containers';
import moment from 'moment';

interface IPackingListHistoryModalProps {
    isOpen: boolean;
    onDismiss(): void;
    onApply(items: ISavedPackingListItem[], mode: 'replace' | 'append'): void;
    onHistoryListChanged?(): void;
    currentOrderId?: string;
    currentContainerId?: string;
    currentGoodsCount?: number;
}

const PackingListHistoryModal: React.FC<IPackingListHistoryModalProps> = (props) => {
    const [selectedPivot, setSelectedPivot] = useState<string>('local');
    const [searchLocal, setSearchLocal] = useState<string>('');
    const [historyList, setHistoryList] = useState<ISavedPackingListHistoryData[]>(PackingListStorageService.getList());
    const [previewItem, setPreviewItem] = useState<ISavedPackingListHistoryData | null>(null);

    // Cross Order state
    const [searchOrderQuery, setSearchOrderQuery] = useState<string>('');
    const [searchingOrders, setSearchingOrders] = useState<boolean>(false);
    const [orderSearchResults, setOrderSearchResults] = useState<IOrderResourceProps[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<IOrderResourceProps | null>(null);
    const [loadingOrderDetails, setLoadingOrderDetails] = useState<boolean>(false);
    const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);

    // Refresh local list when opened
    useEffect(() => {
        if (props.isOpen) {
            setHistoryList(PackingListStorageService.getList());
            setPreviewItem(null);
        }
    }, [props.isOpen]);

    const refreshList = () => {
        const updated = PackingListStorageService.getList();
        setHistoryList(updated);
        if (props.onHistoryListChanged) {
            props.onHistoryListChanged();
        }
    };

    const handleDeleteLocalItem = (id?: string) => {
        if (!id) return;
        PackingListStorageService.remove(id);
        refreshList();
        if (previewItem?.id === id) {
            setPreviewItem(null);
        }
    };

    const handleClearAllLocal = () => {
        PackingListStorageService.clear();
        refreshList();
        setPreviewItem(null);
    };

    // Filter local history
    const filteredHistory = historyList.filter(item => {
        if (!searchLocal.trim()) return true;
        const q = searchLocal.toLowerCase();
        const orderNum = item.orderNumber?.toLowerCase() || '';
        const contNum = item.containerNumber?.toLowerCase() || '';
        const comp = item.companyName?.toLowerCase() || '';
        const cust = item.customerName?.toLowerCase() || '';
        const goodsMatch = (item.goods || []).some(g => 
            (g.goods?.name?.toLowerCase().includes(q)) || 
            (g.supplier?.toLowerCase().includes(q))
        );

        return orderNum.includes(q) || contNum.includes(q) || comp.includes(q) || cust.includes(q) || goodsMatch;
    });

    // Cross Order Search Handler
    const handleSearchOrders = async (query: string) => {
        setSearchOrderQuery(query);
        if (!query || query.trim().length < 2) {
            setOrderSearchResults([]);
            return;
        }

        try {
            setSearchingOrders(true);
            const res = await OrderService.retrieve(`search=${encodeURIComponent(query.trim())}&top=10&paged=1`);
            const items = res?.data || [];
            setOrderSearchResults(items);
        } catch (e) {
            console.error('Failed to search orders', e);
            setOrderSearchResults([]);
        } finally {
            setSearchingOrders(false);
        }
    };

    const handleSelectOrder = async (orderShort: IOrderResourceProps) => {
        try {
            setLoadingOrderDetails(true);
            const fullOrder = await OrderService.get(orderShort.id);
            setSelectedOrder(fullOrder);
            if (fullOrder.containers && fullOrder.containers.length > 0) {
                // Select first container that has goods, or the first one
                const contWithGoods = fullOrder.containers.find(c => (c.goods || []).length > 0);
                setSelectedContainerId(contWithGoods ? contWithGoods.id : fullOrder.containers[0].id);
            } else {
                setSelectedContainerId(null);
            }
        } catch (e) {
            console.error('Failed to load full order details', e);
        } finally {
            setLoadingOrderDetails(false);
        }
    };

    const activeContainerInCrossOrder: IOrderContainerResourceShortProps | undefined = selectedOrder?.containers?.find(
        c => c.id === selectedContainerId
    );

    const crossOrderGoodsCount = (activeContainerInCrossOrder?.goods || []).length;

    const handleApplyCrossOrderGoods = (mode: 'replace' | 'append') => {
        if (!activeContainerInCrossOrder || !activeContainerInCrossOrder.goods) return;

        const convertedItems: ISavedPackingListItem[] = activeContainerInCrossOrder.goods.map((g, idx) => ({
            id: GeneralService.guid(),
            goods: g.goods,
            supplier: g.supplier || g.goods?.supplier || '',
            deliveryDate: (g as any).deliveryDate || '',
            quantityAmount: g.quantityAmount ? String(g.quantityAmount) : '',
            quantityUnit: g.quantityUnit || g.goods?.quantityUnit || '',
            length: g.length ? String(g.length) : '',
            width: g.width ? String(g.width) : '',
            height: g.height ? String(g.height) : '',
            weightAmount: g.weightAmount ? String(g.weightAmount) : '',
            weightUnit: g.weightUnit || g.goods?.weightUnit || 'kg',
            useTotalVolume: g.useTotalVolume || false,
            orderNumber: idx + 1
        }));

        props.onApply(convertedItems, mode);
        props.onDismiss();
    };

    const getSourceBadge = (source: 'saved' | 'draft' | 'tally_scan') => {
        if (source === 'saved') {
            return (
                <span style={{ fontSize: 11, fontWeight: 600, color: '#15803d', backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: 4 }}>
                    ✓ Disimpan
                </span>
            );
        }
        if (source === 'tally_scan') {
            return (
                <span style={{ fontSize: 11, fontWeight: 600, color: '#7e22ce', backgroundColor: '#f3e8ff', padding: '2px 8px', borderRadius: 4 }}>
                    🤖 Scan AI Tally
                </span>
            );
        }
        return (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#b45309', backgroundColor: '#fef3c7', padding: '2px 8px', borderRadius: 4 }}>
                ⏱ Draft Lokal
            </span>
        );
    };

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={props.onDismiss}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Riwayat & Salin Packing List',
                subText: 'Kelola riwayat pengisian packing list atau salin daftar barang dari order lain ke dalam kontainer saat ini.',
            }}
            modalProps={{
                isBlocking: false,
                styles: {
                    main: {
                        maxWidth: '840px !important',
                        width: '840px',
                        borderRadius: 12,
                    }
                }
            }}
        >
            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { minHeight: 460, maxHeight: '72vh' } }}>
                <Pivot
                    selectedKey={selectedPivot}
                    onLinkClick={(item) => setSelectedPivot(item?.props.itemKey || 'local')}
                    styles={{
                        root: { borderBottom: '1px solid #e2e8f0', marginBottom: 8 },
                        linkIsSelected: { fontWeight: 700 }
                    }}
                >
                    <PivotItem headerText="Riwayat Input Terakhir (Browser)" itemKey="local" itemIcon="History" />
                    <PivotItem headerText="Salin dari Order Lain (Database)" itemKey="cross_order" itemIcon="Database" />
                </Pivot>

                {/* TAB 1: LOCAL HISTORY */}
                {selectedPivot === 'local' && (
                    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { flex: 1, overflow: 'hidden' } }}>
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 10 }}>
                            <Stack.Item grow={1}>
                                <SearchBox
                                    placeholder="Cari berdasarkan No. Order, Kontainer, PT, atau Nama Barang..."
                                    value={searchLocal}
                                    onChange={(_, val) => setSearchLocal(val || '')}
                                    styles={{ root: { borderRadius: 6 } }}
                                />
                            </Stack.Item>
                            {historyList.length > 0 && (
                                <DefaultButton
                                    text="Hapus Semua Riwayat"
                                    iconProps={{ iconName: 'Delete' }}
                                    onClick={handleClearAllLocal}
                                    styles={{
                                        root: { color: '#dc2626', borderColor: '#fca5a5' },
                                        rootHovered: { backgroundColor: '#fee2e2', color: '#b91c1c' }
                                    }}
                                />
                            )}
                        </Stack>

                        <Stack tokens={{ childrenGap: 10 }} styles={{ root: { overflowY: 'auto', flex: 1, paddingRight: 6 } }}>
                            {filteredHistory.length === 0 ? (
                                <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { padding: '50px 20px', color: '#6b7280' } }}>
                                    <Text size="medium" style={{ color: '#9ca3af' }}>
                                        {historyList.length === 0 
                                            ? 'Belum ada riwayat pengisian packing list yang tersimpan di browser ini.' 
                                            : 'Tidak ditemukan riwayat yang sesuai dengan pencarian.'}
                                    </Text>
                                </Stack>
                            ) : (
                                filteredHistory.map((item, idx) => {
                                    const timeAgo = moment(item.savedAt).fromNow();
                                    const isPreviewing = previewItem?.id === item.id;

                                    return (
                                        <Stack
                                            key={item.id || idx}
                                            styles={{
                                                root: {
                                                    border: isPreviewing ? '2px solid #2563eb' : '1px solid #e5e7eb',
                                                    borderRadius: 8,
                                                    padding: '12px 14px',
                                                    backgroundColor: isPreviewing ? '#f0f9ff' : '#ffffff',
                                                    transition: 'all 0.15s ease',
                                                    ':hover': {
                                                        borderColor: '#3b82f6',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                                    }
                                                }
                                            }}
                                            tokens={{ childrenGap: 8 }}
                                        >
                                            <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                                                <Stack tokens={{ childrenGap: 4 }}>
                                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                                        <Label size="medium" style={{ fontWeight: 700, color: '#111827' }}>
                                                            {item.orderNumber ? `Order #${item.orderNumber}` : 'Order Tanpa Nomor'}
                                                            {item.containerNumber ? ` - ${item.containerNumber}` : ''}
                                                        </Label>
                                                        {idx === 0 && (
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', backgroundColor: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>
                                                                Paling Baru
                                                            </span>
                                                        )}
                                                        {getSourceBadge(item.source)}
                                                    </Stack>
                                                    <Text size="small" style={{ color: '#4b5563' }}>
                                                        {item.companyName ? `PT: ${item.companyName}` : ''} 
                                                        {item.customerName ? ` • Customer: ${item.customerName}` : ''}
                                                    </Text>
                                                </Stack>

                                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                                                    <Text size="xsmall" style={{ color: '#9ca3af' }}>
                                                        {timeAgo} ({moment(item.savedAt).format('DD/MM HH:mm')})
                                                    </Text>
                                                    <IconButton
                                                        iconProps={{ iconName: 'Delete' }}
                                                        title="Hapus riwayat ini"
                                                        onClick={() => handleDeleteLocalItem(item.id)}
                                                        styles={{
                                                            root: { color: '#9ca3af', width: 28, height: 28 },
                                                            rootHovered: { color: '#dc2626', backgroundColor: '#fee2e2' }
                                                        }}
                                                    />
                                                </Stack>
                                            </Stack>

                                            <Separator styles={{ root: { padding: '2px 0' } }} />

                                            {/* Summary Metrics & Actions */}
                                            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap tokens={{ childrenGap: 8 }}>
                                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', backgroundColor: '#dbeafe', padding: '3px 8px', borderRadius: 4 }}>
                                                        📦 {item.itemCount} Barang
                                                    </span>
                                                    {item.totalQuantity > 0 && (
                                                        <Text size="xsmall" style={{ color: '#64748b' }}>
                                                            Total Qty: <strong>{GeneralService.getNumberWithSeparator(item.totalQuantity)}</strong>
                                                        </Text>
                                                    )}
                                                    {item.totalVolumeM3 > 0 && (
                                                        <Text size="xsmall" style={{ color: '#64748b' }}>
                                                            Vol: <strong>{item.totalVolumeM3} m³</strong>
                                                        </Text>
                                                    )}
                                                    {item.totalWeightKg > 0 && (
                                                        <Text size="xsmall" style={{ color: '#64748b' }}>
                                                            Berat: <strong>{GeneralService.getNumberWithSeparator(item.totalWeightKg)} kg</strong>
                                                        </Text>
                                                    )}
                                                </Stack>

                                                <Stack horizontal tokens={{ childrenGap: 6 }}>
                                                    <DefaultButton
                                                        text={isPreviewing ? "Tutup Preview" : "Preview Barang"}
                                                        iconProps={{ iconName: isPreviewing ? 'ChevronUp' : 'ChevronDown' }}
                                                        styles={{ root: { height: 30, padding: '0 8px', fontSize: 12 } }}
                                                        onClick={() => setPreviewItem(isPreviewing ? null : item)}
                                                    />
                                                    <DefaultButton
                                                        text="+ Tambahkan ke Bawah"
                                                        styles={{
                                                            root: { height: 30, padding: '0 8px', fontSize: 12, borderColor: '#0284c7', color: '#0369a1' },
                                                            rootHovered: { backgroundColor: '#e0f2fe' }
                                                        }}
                                                        onClick={() => {
                                                            props.onApply(item.goods, 'append');
                                                            props.onDismiss();
                                                        }}
                                                    />
                                                    <PrimaryButton
                                                        text={`Gunakan Semua (${item.itemCount} Barang)`}
                                                        styles={{
                                                            root: { height: 30, padding: '0 10px', fontSize: 12, backgroundColor: '#16a34a', borderColor: '#16a34a' },
                                                            rootHovered: { backgroundColor: '#15803d', borderColor: '#15803d' }
                                                        }}
                                                        onClick={() => {
                                                            props.onApply(item.goods, 'replace');
                                                            props.onDismiss();
                                                        }}
                                                    />
                                                </Stack>
                                            </Stack>

                                            {/* Expandable Preview Table */}
                                            {isPreviewing && (
                                                <Stack styles={{
                                                    root: {
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid #bfdbfe',
                                                        borderRadius: 6,
                                                        padding: 8,
                                                        marginTop: 6,
                                                        maxHeight: 220,
                                                        overflowY: 'auto'
                                                    }
                                                }}>
                                                    <Text size="small" style={{ fontWeight: 600, color: '#1e3a8a', marginBottom: 6 }}>
                                                        Daftar {item.goods.length} Barang dalam Riwayat Ini:
                                                    </Text>
                                                    <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', textAlign: 'left' }}>
                                                        <thead>
                                                            <tr style={{ backgroundColor: '#f1f5f9', color: '#475569', borderBottom: '1px solid #cbd5e1' }}>
                                                                <th style={{ padding: '4px 6px' }}>No</th>
                                                                <th style={{ padding: '4px 6px' }}>Nama Barang</th>
                                                                <th style={{ padding: '4px 6px' }}>Supplier</th>
                                                                <th style={{ padding: '4px 6px' }}>Qty</th>
                                                                <th style={{ padding: '4px 6px' }}>Dimensi (PxLxT)</th>
                                                                <th style={{ padding: '4px 6px' }}>Berat</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {item.goods.map((g, gIdx) => (
                                                                <tr key={gIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                    <td style={{ padding: '4px 6px' }}>{gIdx + 1}</td>
                                                                    <td style={{ padding: '4px 6px', fontWeight: 600 }}>{g.goods?.name || '-'}</td>
                                                                    <td style={{ padding: '4px 6px' }}>{g.supplier || '-'}</td>
                                                                    <td style={{ padding: '4px 6px' }}>{g.quantityAmount} {g.quantityUnit}</td>
                                                                    <td style={{ padding: '4px 6px' }}>{g.length || 0}x{g.width || 0}x{g.height || 0} cm</td>
                                                                    <td style={{ padding: '4px 6px' }}>{g.weightAmount ? `${g.weightAmount} ${g.weightUnit || 'kg'}` : '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </Stack>
                                            )}
                                        </Stack>
                                    );
                                })
                            )}
                        </Stack>
                    </Stack>
                )}

                {/* TAB 2: CROSS ORDER IMPORT */}
                {selectedPivot === 'cross_order' && (
                    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { flex: 1, overflow: 'hidden' } }}>
                        <Stack styles={{ root: { backgroundColor: '#f0f9ff', padding: '10px 14px', borderRadius: 6, border: '1px solid #bae6fd' } }}>
                            <Text size="small" style={{ color: '#0369a1', lineHeight: '1.45' }}>
                                <strong>Salin Daftar Barang Antar-Order:</strong> Cari nomor order atau nama customer sumber di bawah ini untuk menyalin seluruh daftar barang ke kontainer saat ini tanpa perlu input ulang.
                            </Text>
                        </Stack>

                        {/* Search Order input */}
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                            <Stack.Item grow={1}>
                                <SearchBox
                                    placeholder="Ketik Nomor Order (misal: 0846) atau Nama Customer..."
                                    value={searchOrderQuery}
                                    onChange={(_, val) => handleSearchOrders(val || '')}
                                    styles={{ root: { borderRadius: 6 } }}
                                />
                            </Stack.Item>
                            {searchingOrders && <Spinner size={SpinnerSize.medium} />}
                        </Stack>

                        <Stack horizontal tokens={{ childrenGap: 14 }} styles={{ root: { flex: 1, minHeight: 300, overflow: 'hidden' } }}>
                            {/* Left Pane: Order Results */}
                            <Stack styles={{ root: { width: '42%', borderRight: '1px solid #e2e8f0', paddingRight: 10, overflowY: 'auto' } }} tokens={{ childrenGap: 6 }}>
                                <Text size="xsmall" style={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                                    Hasil Pencarian Order:
                                </Text>

                                {orderSearchResults.length === 0 ? (
                                    <Stack verticalAlign="center" horizontalAlign="center" styles={{ root: { padding: '40px 10px', color: '#94a3b8' } }}>
                                        <Text size="small">
                                            {searchOrderQuery.trim().length < 2 
                                                ? 'Ketik minimal 2 karakter untuk mencari order.' 
                                                : 'Tidak ditemukan order yang cocok.'}
                                        </Text>
                                    </Stack>
                                ) : (
                                    orderSearchResults.map(ord => {
                                        const isSelected = selectedOrder?.id === ord.id;
                                        const isCurrentOrder = ord.id === props.currentOrderId;
                                        const containerCount = (ord as any).containers?.length || 0;

                                        return (
                                            <Stack
                                                key={ord.id}
                                                onClick={() => handleSelectOrder(ord)}
                                                styles={{
                                                    root: {
                                                        padding: '10px 12px',
                                                        borderRadius: 6,
                                                        cursor: 'pointer',
                                                        border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                                                        backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                                                        transition: 'all 0.1s ease',
                                                        ':hover': { backgroundColor: '#f8fafc', borderColor: '#3b82f6' }
                                                    }
                                                }}
                                                tokens={{ childrenGap: 3 }}
                                            >
                                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                                    <Text size="small" style={{ fontWeight: 700, color: '#0f172a' }}>
                                                        Order #{ord.orderNumber}
                                                    </Text>
                                                    {isCurrentOrder && (
                                                        <span style={{ fontSize: 10, color: '#64748b', backgroundColor: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>
                                                            Order Saat Ini
                                                        </span>
                                                    )}
                                                </Stack>
                                                <Text size="xsmall" style={{ color: '#475569' }}>
                                                    {ord.company?.name || ord.customer?.name || 'Tanpa Nama PT'}
                                                </Text>
                                                <Text size="xsmall" style={{ color: '#94a3b8' }}>
                                                    {moment(ord.createdAt).format('DD MMM YYYY')} • {containerCount} Kontainer
                                                </Text>
                                            </Stack>
                                        );
                                    })
                                )}
                            </Stack>

                            {/* Right Pane: Selected Order Containers & Goods Preview */}
                            <Stack styles={{ root: { flex: 1, overflowY: 'auto', paddingLeft: 4 } }} tokens={{ childrenGap: 10 }}>
                                {loadingOrderDetails ? (
                                    <Stack verticalAlign="center" horizontalAlign="center" styles={{ root: { padding: 50 } }}>
                                        <Spinner size={SpinnerSize.large} label="Memuat kontainer & barang dari order..." />
                                    </Stack>
                                ) : !selectedOrder ? (
                                    <Stack verticalAlign="center" horizontalAlign="center" styles={{ root: { padding: '50px 20px', color: '#94a3b8' } }}>
                                        <Text size="small">Pilih salah satu order di sebelah kiri untuk melihat kontainer dan daftar barangnya.</Text>
                                    </Stack>
                                ) : (
                                    <Stack tokens={{ childrenGap: 10 }}>
                                        <Stack styles={{ root: { backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: 6, border: '1px solid #e2e8f0' } }}>
                                            <Label size="small" style={{ fontWeight: 700, color: '#1e293b' }}>
                                                Pilih Kontainer Asal di Order #{selectedOrder.orderNumber}:
                                            </Label>
                                            <Dropdown
                                                selectedKey={selectedContainerId || undefined}
                                                onChange={(_, opt) => setSelectedContainerId(String(opt?.key || ''))}
                                                placeholder="Pilih Kontainer"
                                                options={(selectedOrder.containers || []).map(c => ({
                                                    key: c.id,
                                                    text: `${c.containerNumber ? c.containerNumber : 'Kontainer'} (${(c.goods || []).length} barang terisi)`
                                                }))}
                                                styles={{ root: { marginTop: 4 } }}
                                            />
                                        </Stack>

                                        {/* Container Goods List preview */}
                                        {activeContainerInCrossOrder && (
                                            <Stack tokens={{ childrenGap: 8 }}>
                                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                                    <Text size="small" style={{ fontWeight: 700, color: '#334155' }}>
                                                        Ditemukan {crossOrderGoodsCount} barang di kontainer ini:
                                                    </Text>
                                                </Stack>

                                                {crossOrderGoodsCount === 0 ? (
                                                    <Stack styles={{ root: { padding: '24px 10px', textAlign: 'center', color: '#94a3b8' } }}>
                                                        <Text size="small">Kontainer ini belum memiliki barang terisi.</Text>
                                                    </Stack>
                                                ) : (
                                                    <>
                                                        <Stack styles={{
                                                            root: {
                                                                maxHeight: 180,
                                                                overflowY: 'auto',
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: 6,
                                                                padding: 6
                                                            }
                                                        }}>
                                                            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', textAlign: 'left' }}>
                                                                <thead>
                                                                    <tr style={{ backgroundColor: '#f1f5f9', color: '#475569' }}>
                                                                        <th style={{ padding: '4px 6px' }}>No</th>
                                                                        <th style={{ padding: '4px 6px' }}>Barang</th>
                                                                        <th style={{ padding: '4px 6px' }}>Supplier</th>
                                                                        <th style={{ padding: '4px 6px' }}>Qty</th>
                                                                        <th style={{ padding: '4px 6px' }}>Dimensi (PxLxT)</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(activeContainerInCrossOrder.goods || []).map((g, idx) => (
                                                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                            <td style={{ padding: '4px 6px' }}>{idx + 1}</td>
                                                                            <td style={{ padding: '4px 6px', fontWeight: 600 }}>{g.goods?.name || '-'}</td>
                                                                            <td style={{ padding: '4px 6px' }}>{g.supplier || '-'}</td>
                                                                            <td style={{ padding: '4px 6px' }}>{g.quantityAmount} {g.quantityUnit}</td>
                                                                            <td style={{ padding: '4px 6px' }}>{g.length || 0}x{g.width || 0}x{g.height || 0} cm</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </Stack>

                                                        {/* Action Buttons to apply to current container */}
                                                        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 6 } }}>
                                                            <DefaultButton
                                                                text="+ Tambahkan ke Bawah"
                                                                styles={{
                                                                    root: { flex: 1, height: 32, borderColor: '#0284c7', color: '#0369a1' },
                                                                    rootHovered: { backgroundColor: '#e0f2fe' }
                                                                }}
                                                                onClick={() => handleApplyCrossOrderGoods('append')}
                                                            />
                                                            <PrimaryButton
                                                                text={`Salin Semua (${crossOrderGoodsCount} Barang)`}
                                                                styles={{
                                                                    root: { flex: 1, height: 32, backgroundColor: '#16a34a', borderColor: '#16a34a' },
                                                                    rootHovered: { backgroundColor: '#15803d', borderColor: '#15803d' }
                                                                }}
                                                                onClick={() => handleApplyCrossOrderGoods('replace')}
                                                            />
                                                        </Stack>
                                                    </>
                                                )}
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                )}
            </Stack>

            <DialogFooter>
                <DefaultButton text="Tutup" onClick={props.onDismiss} />
            </DialogFooter>
        </Dialog>
    );
};

export default PackingListHistoryModal;
